'use client';

import { useState, useMemo, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Check } from 'lucide-react';
import { UserCountSelector } from './UserCountSelector';

interface PackFixeVoipCardProps {
  isAnnual: boolean;
}

const TIERS = {
  start: { monthly: 19.9, yearly: 199, label: 'Forfait Start illimité' },
  pro: { monthly: 39.9, yearly: 399, label: 'Forfait Pro illimité' },
} as const;

const DESTINATIONS: Record<string, string> = {
  start: 'Fixes & Mobiles en France Métropolitaine',
  pro: 'Fixes & Mobiles en France Métropolitaine, DOM-TOM et International',
};

const getFeatures = (userCount: number, destination: string) => [
  '1 numéro fixe France (01, 02, 03, 04, 05, 09 et DOM-TOM) ou conservez le vôtre avec la portabilité',
  `${userCount} utilisateur${userCount > 1 ? 's' : ''} inclus (facturation par utilisateur)`,
  `Appels entrants et sortants illimités vers ${destination}`,
  'Abonnement sans engagement',
  'Serveur vocal interactif: Pré-décroché, musique d’attente, renvoi d’appels, gestion des horaires, etc.',
  'Application Mobile et Ordinateur',
];

export function PackFixeVoipCard({ isAnnual }: PackFixeVoipCardProps) {
  const router = useRouter();
  const [tier, setTier] = useState<'start' | 'pro'>('start');
  const [userCount, setUserCount] = useState(1);
  const [isNavigating, setIsNavigating] = useState(false);

  const unit = TIERS[tier];
  const unitPrice = isAnnual ? unit.yearly : unit.monthly;
  const totalAfterTrial = unitPrice * userCount;
  const period = isAnnual ? 'an' : 'mois';

  const destination = DESTINATIONS[tier];
  const features = useMemo(() => getFeatures(userCount, destination), [userCount, destination]);

  const commandeHref = useMemo(() => {
    const map = {
      start: isAnnual ? 'voip_start_y' : 'voip_start_m',
      pro: isAnnual ? 'voip_pro_y' : 'voip_pro_m',
    } as const;
    const offer = map[tier];
    return `/commande?type=telephonieIp&offer=${offer}&users=${userCount}`;
  }, [tier, userCount, isAnnual]);

  useEffect(() => {
    router.prefetch(commandeHref);
  }, [router, commandeHref]);

  const handleSubscribe = () => {
    setIsNavigating(true);
    router.push(commandeHref);
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg border-2 border-primaryBlue overflow-hidden flex flex-col h-full">
      <div className="bg-primaryBlue text-white p-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
            <img src="/pack-fixe.png" alt="" className="w-10 h-10 object-contain" />
          </div>
          <div>
            <h3 className="text-xl font-bold">Pack Fixe VoIP Illimité</h3>
          </div>
        </div>
      </div>

      <div className="p-6 flex-1 flex flex-col">
        <div className="mb-4 text-center">
          <p className="text-sm text-gray-600 mb-3">Choisissez votre forfait :</p>
          <div className="flex rounded-full bg-lightGreen p-1 gap-1">
            {(['start', 'pro'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTier(t)}
                className={`flex-1 py-2 px-3 rounded-full text-sm font-medium transition-colors ${
                  tier === t ? 'bg-primaryBlue text-white' : 'text-gray-700 hover:bg-white/80'
                }`}
              >
                {TIERS[t].label}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-6 text-center">
          <UserCountSelector
            value={userCount}
            onChange={setUserCount}
            min={1}
            max={50}
            colorScheme="blue"
          />
        </div>

        <div className="mb-6 text-center space-y-2">
          <p className="text-xs font-medium text-primaryBlue">
            {isAnnual ? 'Annuel' : 'Mensuel'} · {userCount} utilisateur{userCount > 1 ? 's' : ''}
          </p>
          <p className="text-lg text-gray-800 leading-snug">
            <span className="font-bold text-primaryBlue">Essai gratuit</span> pendant 7 jours puis{' '}
            <span className="font-bold">
              {totalAfterTrial.toFixed(2).replace('.', ',')} € HT/{period}
            </span>
          </p>
        </div>

        <ul className="space-y-3 mb-6 flex-1">
          {features.map((feature, index) => (
            <li key={index} className="flex items-start gap-3">
              <Check className="w-5 h-5 text-primaryBlue flex-shrink-0 mt-0.5" />
              <span className="text-sm text-gray-700 text-left">{feature}</span>
            </li>
          ))}
        </ul>

        <button
          type="button"
          onClick={handleSubscribe}
          disabled={isNavigating}
          className="w-full py-3 px-6 bg-primaryBlue text-white font-semibold rounded-full hover:bg-primaryBlue/90 transition-colors disabled:opacity-70"
        >
          {isNavigating ? 'Chargement…' : 'Démarrer mon essai maintenant'}
        </button>
      </div>
    </div>
  );
}
